import { Fragment, lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { dropPrerender, prerenderedPath, showingPrerender } from './lib/prerender-dom.ts';
import { useApp } from './state/app.tsx';
import { CUSTOM_PAGES, PAGES } from './lib/pages.ts';
import { tx, UI } from './lib/i18n.ts';
import { safeNext } from './lib/guest.ts';
import { Shell } from './components/Shell.tsx';
import { Login } from './pages/Login.tsx';
import { Landing } from './pages/Landing.tsx';

/* 页面按需加载（2026-11-01 体检：以前 24 页的代码第一次打开时一起下，首屏约 340 KB）。
   登录页和游客首页第一眼就要看到，照旧直接打包；其余的点到哪一页才下哪一页 */
const HomePage = lazy(() => import('./pages/HomePage.tsx').then(m => ({ default: m.HomePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.tsx').then(m => ({ default: m.SettingsPage })));
const WalletsPage = lazy(() => import('./pages/WalletsPage.tsx').then(m => ({ default: m.WalletsPage })));
const TokensPage = lazy(() => import('./pages/TokensPage.tsx').then(m => ({ default: m.TokensPage })));
const RemindersPage = lazy(() => import('./pages/RemindersPage.tsx').then(m => ({ default: m.RemindersPage })));
const BillingPage = lazy(() => import('./pages/BillingPage.tsx').then(m => ({ default: m.BillingPage })));
const InboxPage = lazy(() => import('./pages/InboxPage.tsx').then(m => ({ default: m.InboxPage })));
const MemoPage = lazy(() => import('./pages/MemoPage.tsx').then(m => ({ default: m.MemoPage })));
const ReferralPage = lazy(() => import('./pages/ReferralPage.tsx').then(m => ({ default: m.ReferralPage })));
const Features = lazy(() => import('./pages/Features.tsx').then(m => ({ default: m.Features })));
const VideoTour = lazy(() => import('./pages/VideoTour.tsx').then(m => ({ default: m.VideoTour })));
const PaperPage = lazy(() => import('./pages/PaperPage.tsx').then(m => ({ default: m.PaperPage })));
const PartnersPage = lazy(() => import('./pages/PartnersPage.tsx').then(m => ({ default: m.PartnersPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage.tsx').then(m => ({ default: m.PrivacyPage })));
const GmgnKeyGuide = lazy(() => import('./pages/GmgnKeyGuide.tsx').then(m => ({ default: m.GmgnKeyGuide })));
const DocsPage = lazy(() => import('./pages/DocsPage.tsx').then(m => ({ default: m.DocsPage })));
const GuidesPage = lazy(() => import('./pages/GuidesPage.tsx').then(m => ({ default: m.GuidesPage })));
const ReminderAction = lazy(() => import('./pages/ReminderAction.tsx').then(m => ({ default: m.ReminderAction })));
const BuddyConsent = lazy(() => import('./pages/BuddyConsent.tsx').then(m => ({ default: m.BuddyConsent })));
const AccountPage = lazy(() => import('./pages/AccountPage.tsx').then(m => ({ default: m.AccountPage })));
const NotBuiltPage = lazy(() => import('./pages/NotBuilt.tsx').then(m => ({ default: m.NotBuiltPage })));
const ExtPage = lazy(() => import('./pages/ExtPage.tsx').then(m => ({ default: m.ExtPage })));
const HeatPage = lazy(() => import('./pages/HeatPage.tsx').then(m => ({ default: m.HeatPage })));

/** 下页面代码那一下（通常不到半秒）的占位。预渲染的正文还在屏幕上的话就不画转圈（lib/prerender-dom.ts） */
function Loading(): React.ReactElement | null {
  if (showingPrerender()) return null;
  return <div className="booting" style={{ minHeight: 240 }}><div className="spin" /></div>;
}

/**
 * 路由。
 *
 * ── 为什么有真路由，而原型只有一个 `S.page` 状态 ──
 * 原型是单文件演示，刷新回到第一页无所谓。真应用里不行：
 *   分享一个页面的链接（客服说「打开这一页看看」）
 *   浏览器的前进后退要能用
 *   刷新之后还在原来那一页
 *
 * 页面路径用 `/p/<清单里的页面 id>`，所以加一页不用改路由 ——
 * 清单里多一个 id，侧栏和路由同时就有了。
 *
 * ── 没登录也能进来 ──────────────────────────────────────
 *   `/`        游客看到产品首页；登录了直接进总览
 *   `/login`   登录页。登录完回到 `?next=` 指的那一页
 *   `/p/…`     所有页面游客都能看（示例数据 + 默认设置），保存时才邀请登录
 * 以前是「没登录 = 整屏登录框」：第一次来的人连这是个什么东西都看不到，
 * 就先被要求交出邮箱。
 */

export function App(): React.ReactElement | null {
  const app = useApp();
  const loc = useLocation();

  /* 预渲染的那份正文（公开页面才有，见 lib/prerender-dom.ts）：
     现在的地址不是它渲染的那一页（登录了被送去总览、点去了别的页）、或者启动出错 / 会话过期，就立刻拿掉。
     地址对得上的时候由那一页自己（LandingShell）画好后拿掉 */
  useLayoutEffect(() => {
    if (app.booting) return;
    const at = prerenderedPath();
    if (at === null) return;
    if (at !== loc.pathname || app.bootError !== null || app.sessionLost) dropPrerender();
  }, [app.booting, app.bootError, app.sessionLost, loc.pathname]);

  /* 启动失败和没登录要分开。
     把服务器故障显示成登录页会让用户以为自己被登出了，
     然后反复登录反复失败，而真正的原因一个字都看不到。 */
  if (app.bootError !== null) {
    return (
      <div className="booting">
        <div className="notbuilt" style={{ maxWidth: 520, textAlign: 'left' }}>
          <b>连不上服务器</b>
          <div style={{ marginTop: 6 }}>{app.bootError}</div>
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn sm" onClick={() => window.location.reload()}>
              重新试一次
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (app.booting) {
    /* 预渲染的正文正显示着：转圈不画，让人先读着 */
    if (showingPrerender()) return null;
    return (
      <div className="booting">
        <div className="spin" />
        <div>{tx(UI.loading, app.lang)}</div>
      </div>
    );
  }

  /* 用着用着会话过期了：整屏登录框，登录完回到原来那一页（地址没变）。
     **不能**悄悄变成游客 —— 见 state/app.tsx 开头 */
  if (app.sessionLost) return <Login expired />;

  return (
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/" element={app.guest ? <Landing /> : <Navigate to="/p/home" replace />} />
      <Route path="/login" element={app.guest ? <Login /> : <AfterLogin />} />
      {/* 功能介绍和视频介绍。**登录了也照样能看** ——
          这两页不只是拉新：老用户想知道「那个功能到底怎么用」时也会来，
          而把已登录的人踢回 /p/home 只会让他以为链接坏了。
          它们不套外框（自己有顶栏和页脚，见 LandingShell）。 */}
      {/* 繁体、英文网址（2026-11-04，lib/locale-paths.ts）：和简体是同几页，语言由网址定（index.html 内联脚本在启动前写好） */}
      {(['/zh-hk', '/en'] as const).map(pf => (
        <Fragment key={pf}>
          <Route path={pf} element={app.guest ? <Landing /> : <Navigate to="/p/home" replace />} />
          <Route path={pf + '/features'} element={<Features />} />
          <Route path={pf + '/video'} element={<VideoTour />} />
          <Route path={pf + '/paper'} element={<PaperPage />} />
          <Route path={pf + '/partners'} element={<PartnersPage />} />
          <Route path={pf + '/privacy'} element={<PrivacyPage />} />
          <Route path={pf + '/gmgn-key'} element={<GmgnKeyGuide />} />
        </Fragment>
      ))}
      <Route path="/features" element={<Features />} />
      <Route path="/video" element={<VideoTour />} />
      {/* 免费的模拟交易扩展：介绍、下载、怎么装。登没登录都能看 */}
      <Route path="/paper" element={<PaperPage />} />
      {/* 常用平台（老板的邀请链接）。只从页脚和账号页进来，见 lib/partners-copy.ts 开头的规矩 */}
      <Route path="/partners" element={<PartnersPage />} />
      {/* 隐私政策：Chrome 应用商店要一个公开的网址（2026-10-30）。登没登录都能看 */}
      <Route path="/privacy" element={<PrivacyPage />} />
      {/* GMGN API Key 图文教程（2026-10-30）。扩展设置页和钱包页链到这里 */}
      <Route path="/gmgn-key" element={<GmgnKeyGuide />} />
      {/* 使用教程（2026-10-30）：每个大功能、小功能都写到，登没登录都能看 */}
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/:id" element={<DocsPage />} />
      {/* 文章（2026-11-04）：回答「上头了怎么办」这类问题的长文，给搜索引擎和 AI 搜索。登没登录都能看 */}
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/guides/:id" element={<GuidesPage />} />
      {/* 提醒里带的一键处理链接。**登没登录都一样**：链接本身就是授权，
          而且不套外框 —— 从 Telegram 点进来的人只想按一下就走 */}
      <Route path="/r/:token" element={<ReminderAction />} />
      {/* 喊话小伙伴的「同意 / 不再接收」。读者**不是我们的用户**，是他的朋友 ——
          所以不要求登录、不套外框、不收任何信息（token 就是授权） */}
      <Route path="/b/:token" element={<BuddyConsent />} />
      <Route element={<ShellLayout />}>
        <Route path="/p/:pageId" element={<PageByid />} />
        <Route path="/account" element={<AccountPage />} />
        {/* 认不出的路径不要白屏，也不要静默跳首页 ——
            静默跳转会让「链接失效」这件事查不出来 */}
        <Route path="*" element={<UnknownRoute />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

/**
 * 外框（侧栏、顶栏、状态栏）用布局路由挂一次。
 * 每个路由各包一层 `<Shell>` 的话，换页时整个外框会卸载重建 ——
 * 侧栏滚动位置丢掉、状态栏的计时器重来。
 */
function ShellLayout(): React.ReactElement {
  return (
    <Shell>
      {/* 换页时只有内容区转一下，侧栏和顶栏不动 */}
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </Shell>
  );
}

/**
 * 已经登录的人落到 `/login` 上：送回 `?next=` 指的那一页。
 *
 * 登录成功那一刻也走这里 —— 登录会把整个应用重新拉一遍（换成他账号里的设置），
 * 拉完之后这个路由重新渲染，于是自然跳回他来的地方。
 * 用声明式的跳转而不是在登录页里 `navigate()`：登录页在那一刻已经被卸载了，
 * 在一个卸载了的组件里跳转是靠不住的。
 */
function AfterLogin(): React.ReactElement {
  const loc = useLocation();
  return <Navigate to={safeNext(loc.search)} replace />;
}

function PageByid(): React.ReactElement {
  const app = useApp();
  const { pageId } = useParams<{ pageId: string }>();
  const id = pageId ?? '';
  const page = PAGES.find(p => p.id === id);

  /* 标签页标题跟着页面走。一排「交易风控卫士」的标签页没法分，
     读屏软件切页时也要靠它报出「现在在哪」 */
  useEffect(() => {
    document.title = page ? `${tx(page.title, app.lang)} · ${tx(UI.appName, app.lang)}` : tx(UI.appName, app.lang);
  }, [page, app.lang]);

  if (!page) return <UnknownRoute />;
  /* 有真数据界面的页面各自一个组件。
     **按 id 一个个分发，不要用 `DATA_PAGES.has(id) ? <某一页>`** ——
     那样往 DATA_PAGES 里加一个页面会让它渲染成另一页的界面，
     而那是个不报错的错：用户在价格监控页上看到钱包列表。
     多一个 if 换掉那个风险，划算。 */
  if (id === 'home') return <HomePage />;
  if (id === 'wallet') return <WalletsPage />;
  if (id === 'tokenwatch') return <TokensPage />;
  if (id === 'reminders') return <RemindersPage />;
  if (id === 'billing') return <BillingPage />;
  if (id === 'inbox') return <InboxPage />;
  if (id === 'memo') return <MemoPage />;
  if (id === 'referral') return <ReferralPage />;
  if (id === 'ext') return <ExtPage />;
  if (id === 'chain') return <HeatPage />;
  if (CUSTOM_PAGES.has(id)) return <NotBuiltPage pageId={id} lang={app.lang} />;
  return <SettingsPage pageId={id} />;
}

function UnknownRoute(): React.ReactElement {
  return (
    <div className="notbuilt">
      <b>没有这个地址。</b>
      {/* 用 <Link> 不用 <a href> —— 后者会整页重新加载，
          于是设置、凭据、外观全部重新拉一遍，白等一秒多 */}
      <div>左边选一页，或者<Link to="/p/rules">回到交易纪律</Link>。</div>
    </div>
  );
}
